import { useState, useMemo, useCallback } from "react";
import { useStore } from "zustand";
import { useBreadcrumbs } from "@app/components/Breadcrumbs";
import {
  ActionButton,
  Button,
  Switch,
  ProgressBar,
  Text,
  TextArea,
  TextField,
  Picker,
  PickerItem,
  SegmentedControl,
  SegmentedControlItem,
  ToastQueue,
} from "@react-spectrum/s2";

import SendIcon from "@react-spectrum/s2/icons/Send";
import CodeIcon from "@react-spectrum/s2/icons/Code";

import { store } from "@app/store";
import {
  useImageLabeling,
  useSystemPrompts,
  useZoltarMutation,
} from "@app/hooks/zoltar";
import {
  IMAGE_LABELING_PROMPT_IDS,
  getZoltarCurl,
  isZoltarGenerateOk,
  parseZoltarGenerateJson,
} from "@app/services/zoltar";

import { SyntaxHighlighter } from "@app/components/SyntaxHighlighter";
import { AVAILABLE_MODELS } from "./Copilot";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

export const PromptPlayground = () => {
  useBreadcrumbs([
    { label: "Admin", href: "/admin" },
    { label: "Zoltar Prompts" },
  ]);

  const prompts = useSystemPrompts();
  const zoltar = useZoltarMutation();
  const labeling = useImageLabeling();

  const [mode, setMode] = useState("generate");
  const [promptId, setPromptId] = useState(null);
  const [promptText, setPromptText] = useState("");
  const [userInput, setUserInput] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [model, setModel] = useState(AVAILABLE_MODELS[0].id);
  const [useSchema, setUseSchema] = useState(true);
  const [curl, setCurl] = useState("");
  const [isCurlPending, setIsCurlPending] = useState(false);
  const userEmail = useStore(store, (state) => state.ims?.profile?.email);

  const isImageMode = mode === "image";
  const request = isImageMode ? labeling : zoltar;

  const selectedPrompt = useMemo(
    () => prompts.data?.find((p) => p._id === promptId),
    [prompts.data, promptId],
  );
  const hasSchema = Boolean(selectedPrompt?.schema);

  const handlePromptChange = useCallback(
    (id) => {
      setPromptId(id);
      setPromptText(prompts.data?.find((p) => p._id === id)?.prompt ?? "");
      setMode(IMAGE_LABELING_PROMPT_IDS.includes(id) ? "image" : "generate");
    },
    [prompts.data],
  );

  const result = useMemo(() => {
    if (isImageMode) return labeling.data ?? {};
    try {
      if (!isZoltarGenerateOk(zoltar.data)) return {};
      return parseZoltarGenerateJson(zoltar.data, "{}");
    } catch (error) {
      console.warn("Failed to parse Zoltar response:", error, zoltar.data);
      return zoltar?.data?.envelope?.text ?? {};
    }
  }, [isImageMode, labeling.data, zoltar.data]);

  const variables = useMemo(() => {
    // An unedited stored prompt is sent by ID, exactly like the app does.
    const isStoredPrompt =
      selectedPrompt && promptText === selectedPrompt.prompt;

    if (isImageMode) {
      return {
        imageUrl: imageUrl.trim(),
        ...(isStoredPrompt
          ? { systemPromptId: selectedPrompt._id }
          : { promptTemplate: promptText }),
        ...(userInput.trim() ? { userInput } : {}),
      };
    }

    const userMessages = userInput.trim()
      ? [{ role: "user", prompt_template: userInput }]
      : [];

    // The stored-prompt path always applies the stored schema.
    if (isStoredPrompt && (useSchema || !hasSchema)) {
      return {
        systemPromptId: selectedPrompt._id,
        promptTemplate: userMessages,
        model,
      };
    }

    return {
      promptTemplate: [
        { role: "system", prompt_template: promptText },
        ...userMessages,
      ],
      model,
      schema: useSchema && hasSchema ? selectedPrompt.schema : undefined,
    };
  }, [
    isImageMode,
    imageUrl,
    promptText,
    userInput,
    model,
    useSchema,
    hasSchema,
    selectedPrompt,
  ]);

  // A fresh object per run: useZoltarMutation keys the agent thread by variables identity.
  const handleRun = useCallback(
    () => request.mutate({ ...variables }),
    [request.mutate, variables],
  );

  const handleCurl = useCallback(async () => {
    setIsCurlPending(true);
    try {
      const command = await getZoltarCurl(
        isImageMode
          ? {
              endpoint: "imageLabeling",
              imageUrl: variables.imageUrl,
              prompt_template: variables.promptTemplate,
              systemPromptId: variables.systemPromptId,
              user_input: variables.userInput,
            }
          : {
              endpoint: "generateContent",
              useAgent: true,
              user: userEmail,
              prompt_template: variables.promptTemplate,
              systemPromptId: variables.systemPromptId,
              model: variables.model,
              schema: variables.schema,
            },
      );
      setCurl(command);
      try {
        await navigator.clipboard.writeText(command);
        ToastQueue.positive("cURL copied", { timeout: 3000 });
      } catch {
        ToastQueue.neutral("cURL generated", { timeout: 3000 });
      }
    } catch (error) {
      ToastQueue.negative(error.message, { timeout: 5000 });
    } finally {
      setIsCurlPending(false);
    }
  }, [isImageMode, variables, userEmail]);

  const isRunDisabled =
    request.isPending ||
    !promptText.trim() ||
    (isImageMode && !imageUrl.trim());

  return (
    <>
      <title>{`Zoltar Prompts | BeStory`}</title>
      <div
        className={style({
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 32,
          boxSizing: "border-box",
          width: "full",
          height: "calc(100vh - 6.5rem)",
          marginX: "auto",
        })}
      >
        <div
          className={style({
            gridColumnStart: 2,
            gridRowStart: 1,
            display: "flex",
            flexDirection: "column",
            gap: 16,
            minHeight: 0,
            overflowY: "auto",
            boxSizing: "border-box",
            padding: 16,
            borderRadius: "xl",
            backgroundColor: "layer-1",
          })}
        >
          {curl && (
            <>
              <div className={style({ font: "detail", color: "neutral-subdued" })}>
                cURL
              </div>
              <code
                className={style({
                  font: "code-sm",
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                })}
              >
                {curl}
              </code>
            </>
          )}
          <div className={style({ font: "detail", color: "neutral-subdued" })}>
            Response
          </div>
          {request.isIdle && (
            <div
              className={style({
                size: "full",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                font: "body",
                color: "neutral-subdued",
              })}
            >
              Pick a prompt, adjust it, add user input and hit Run.
            </div>
          )}
          {request.isPending && (
            <div
              className={style({
                size: "full",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              })}
            >
              <div
                className={style({
                  display: "flex",
                  alignItems: "end",
                  gap: 24,
                  marginX: "auto",
                })}
              >
                <ProgressBar isIndeterminate label="Generating response" />
                <ActionButton onPress={request.reset}>Cancel</ActionButton>
              </div>
            </div>
          )}
          {request.isSuccess &&
            (typeof result === "string" ? (
              <code
                className={style({
                  font: "code",
                  whiteSpace: "pre-wrap",
                  alignSelf: "start",
                })}
              >
                {result}
              </code>
            ) : (
              <SyntaxHighlighter
                language="json"
                code={JSON.stringify(result, null, 2)}
              />
            ))}
          {request.isError && (
            <code
              className={style({
                font: "code",
                whiteSpace: "pre-wrap",
                alignSelf: "start",
              })}
            >
              {request.error?.message}
              {"\n"}
              {JSON.stringify(
                request.error?.cause?.data ?? request.error,
                null,
                2,
              )}
            </code>
          )}
        </div>

        <div
          className={style({
            gridColumnStart: 1,
            gridRowStart: 1,
            display: "flex",
            flexDirection: "column",
            gap: 24,
            minHeight: 0,
            overflowY: "auto",
          })}
        >
          <div
            className={style({
              display: "flex",
              flexDirection: "column",
              gap: 24,
              flexGrow: 1,
            })}
          >
            <Picker
              label="Zoltar prompt"
              placeholder={
                prompts.isLoading ? "Loading prompts…" : "Select a prompt"
              }
              selectedKey={promptId}
              onSelectionChange={handlePromptChange}
              isDisabled={prompts.isLoading || request.isPending}
              isInvalid={prompts.isError}
              errorMessage={prompts.error?.message}
              styles={style({ width: "full" })}
            >
              {(prompts.data ?? []).map((p) => (
                <PickerItem key={p._id} id={p._id}>
                  <Text slot="label">{p._id}</Text>
                  {IMAGE_LABELING_PROMPT_IDS.includes(p._id) && (
                    <Text slot="description">Image labeling</Text>
                  )}
                </PickerItem>
              ))}
            </Picker>
            <TextArea
              label="System prompt"
              value={promptText}
              onChange={setPromptText}
              isDisabled={request.isPending}
              styles={style({ flexGrow: 1, padding: 0 })}
              UNSAFE_className="__PromptInput"
            />
            <TextArea
              label="User input"
              placeholder={"COMPANY_NAME: Adobe\nPRODUCT: Photoshop"}
              value={userInput}
              onChange={setUserInput}
              isDisabled={request.isPending}
              styles={style({ flexGrow: 1, padding: 0 })}
              UNSAFE_className="__PromptInput __PromptInput--narrow"
            />
            {isImageMode && (
              <TextField
                label="Image URL"
                type="url"
                placeholder="https://…"
                value={imageUrl}
                onChange={setImageUrl}
                isDisabled={request.isPending}
                isRequired
                styles={style({ width: "full" })}
              />
            )}
          </div>
          <div
            className={style({
              display: "flex",
              flexWrap: "wrap",
              alignItems: "end",
              gap: 16,
            })}
          >
            <SegmentedControl
              aria-label="Endpoint"
              selectedKey={mode}
              onSelectionChange={setMode}
              isDisabled={request.isPending}
            >
              <SegmentedControlItem id="generate">Generate</SegmentedControlItem>
              <SegmentedControlItem id="image">Image labeling</SegmentedControlItem>
            </SegmentedControl>
            {!isImageMode && (
              <>
                <Picker
                  label="Model"
                  selectedKey={model}
                  onSelectionChange={setModel}
                  isDisabled={request.isPending}
                >
                  {AVAILABLE_MODELS.map((m) => (
                    <PickerItem key={m.id} id={m.id}>
                      <Text slot="label">{m.name}</Text>
                      <Text slot="description">{m.description}</Text>
                    </PickerItem>
                  ))}
                </Picker>
                <Switch
                  isSelected={useSchema && hasSchema}
                  onChange={setUseSchema}
                  isDisabled={!hasSchema}
                >
                  Use prompt schema
                </Switch>
              </>
            )}
            <Button
              variant="accent"
              onPress={handleRun}
              isDisabled={isRunDisabled}
            >
              <SendIcon />
              <Text>Run</Text>
            </Button>
            <ActionButton
              size="S"
              onPress={handleCurl}
              isDisabled={isRunDisabled || isCurlPending}
            >
              <CodeIcon />
              <Text>cURL</Text>
            </ActionButton>
          </div>
        </div>
      </div>
    </>
  );
};
